import { markAdminDataCacheStale } from "../utils/adminDataCache";
import { markPublicDataCacheStale } from "../utils/publicDataCache";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000/api";

const API = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

const shouldUseSameOriginPublicBridge = () => {
  if (process.env.NODE_ENV !== "production") {
    return false;
  }

  if (typeof window === "undefined") {
    return true;
  }

  const hostname = String(window.location.hostname || "").toLowerCase();

  return !["localhost", "127.0.0.1", "::1"].includes(hostname);
};

const requestSameOriginPublicData = async (
  resource,
  params,
  fallbackFactory,
  extraConfig = {},
) => {
  if (!shouldUseSameOriginPublicBridge()) {
    return fallbackFactory();
  }

  try {
    return await axios.get("/public-data.php", {
      params: { resource, ...params },
      timeout: 8000,
      ...extraConfig,
    });
  } catch (error) {
    if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
      throw error;
    }

    return fallbackFactory();
  }
};

/*
 * Development React StrictMode mounts components twice. Without request
 * coalescing that can send identical public GETs to MongoDB twice at the same
 * time. Keep a tiny in-flight/response cache so duplicate reads share one
 * request while normal fresh navigation is still revalidated quickly.
 */
const PUBLIC_GET_DEDUPE_WINDOW_MS = 1500;
const publicGetInflight = new Map();
const publicGetRecent = new Map();
const adminGetInflight = new Map();
const adminGetRecent = new Map();
const ADMIN_GET_DEDUPE_WINDOW_MS = 2000;

const dedupePublicGet = (key, requestFactory) => {
  const now = Date.now();
  const recent = publicGetRecent.get(key);

  if (recent && now - recent.savedAt <= PUBLIC_GET_DEDUPE_WINDOW_MS) {
    return Promise.resolve(recent.response);
  }

  const inflight = publicGetInflight.get(key);

  if (inflight) {
    return inflight;
  }

  const request = Promise.resolve()
    .then(requestFactory)
    .then((response) => {
      publicGetRecent.set(key, { response, savedAt: Date.now() });
      return response;
    })
    .finally(() => {
      publicGetInflight.delete(key);
    });

  publicGetInflight.set(key, request);
  return request;
};

/*
 * V4 uses an HttpOnly cookie session. The token argument remains in the
 * public function signatures so existing admin components do not need a
 * risky large-scale rewrite, but it is never exposed as an HTTP header.
 */
const authConfig = () => ({
  withCredentials: true,
});

const sleep = (delayMs) =>
  new Promise((resolve) => window.setTimeout(resolve, delayMs));

const getRetryDelay = (error, attempt) => {
  const retryAfter = Number.parseInt(
    error?.response?.headers?.["retry-after"],
    10,
  );

  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return Math.min(retryAfter * 1000, 5000);
  }

  return attempt === 1 ? 450 : 1100;
};

const shouldRetryPublicRequest = (error) => {
  const config = error?.config;

  if (!config?.__publicRetry || String(config.method || "get").toLowerCase() !== "get") {
    return false;
  }

  if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
    return false;
  }

  const status = error?.response?.status;

  return (
    !status ||
    status === 408 ||
    status === 425 ||
    status === 429 ||
    status === 500 ||
    status === 502 ||
    status === 503 ||
    status === 504
  );
};

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const retryCount = Number(config.__publicRetryCount || 0);

    if (shouldRetryPublicRequest(error) && retryCount < 2) {
      config.__publicRetryCount = retryCount + 1;
      await sleep(getRetryDelay(error, retryCount + 1));
      return API.request(config);
    }

    const isAuthenticatedRequest = config.withCredentials === true;
    const isLoginRequest = String(config.url || "").includes("/auth/login");

    if (
      typeof window !== "undefined" &&
      error.response?.status === 401 &&
      isAuthenticatedRequest &&
      !isLoginRequest
    ) {
      window.dispatchEvent(new Event("nexrojgar:auth-expired"));
    }

    return Promise.reject(error);
  },
);


const dedupeAdminGet = (key, requestFactory) => {
  const now = Date.now();
  const recent = adminGetRecent.get(key);

  if (recent && now - recent.savedAt <= ADMIN_GET_DEDUPE_WINDOW_MS) {
    return Promise.resolve(recent.response);
  }

  const inflight = adminGetInflight.get(key);
  if (inflight) return inflight;

  const request = Promise.resolve()
    .then(requestFactory)
    .then((response) => {
      adminGetRecent.set(key, { response, savedAt: Date.now() });
      return response;
    })
    .finally(() => {
      adminGetInflight.delete(key);
    });

  adminGetInflight.set(key, request);
  return request;
};

const invalidateAdminReadCaches = () => {
  adminGetInflight.clear();
  adminGetRecent.clear();
  markAdminDataCacheStale();
  markPublicDataCacheStale();
};

const afterAdminMutation = (request) =>
  request.then((response) => {
    invalidateAdminReadCaches();
    return response;
  });

/* =========================
   Authentication
========================= */

export const loginUser = (
  credentials,
) =>
  API.post(
    "/auth/login",
    credentials,
    authConfig(),
  );

export const getSession = () =>
  API.get("/auth/session", authConfig());

export const logoutUser = () =>
  API.post("/auth/logout", {}, authConfig());

export const submitContactMessage = (payload) =>
  API.post("/contact", payload, {
    timeout: 12000,
  });

/*
 * Public signup disabled hai,
 * isliye signup API intentionally removed.
 */
// export const signupUser = (formData) => API.post("/auth/signup", formData);


/*
 * Authenticated user apna password
 * change kar sakta hai.
 */
export const changePassword = (
  passwordData,
  token,
) =>
  API.patch(
    "/auth/change-password",
    passwordData,
    authConfig(token),
  );

/* =========================
   Public posts
========================= */

export const getPosts = ({
  page = 1,
  limit = 10,
  search = "",
  category = "",
} = {}) => {
  const params = { page, limit, search, category };
  const key = `posts:${page}:${limit}:${search}:${category}`;

  return dedupePublicGet(key, () =>
    requestSameOriginPublicData(
      "list",
      params,
      () => API.get("/posts", { params, __publicRetry: true }),
    ),
  );
};

export const getPostsByCategory = (category) => {
  const encodedCategory = encodeURIComponent(category);

  return dedupePublicGet(`category:${category}`, () =>
    requestSameOriginPublicData(
      "category",
      { category },
      () => API.get(`/posts/category/${encodedCategory}`, { __publicRetry: true }),
    ),
  );
};

export const getPostBySlug = (slug) => {
  const encodedSlug = encodeURIComponent(slug);

  return dedupePublicGet(`post:${slug}`, () =>
    API.get(`/posts/detail/${encodedSlug}`, { __publicRetry: true }),
  );
};

export const searchPosts = (
  query,
  {
    limit = 20,
    includeSnippet = true,
    signal,
  } = {},
) => {
  const params = {
    q: query,
    limit,
    includeSnippet: includeSnippet ? 1 : 0,
  };

  return requestSameOriginPublicData(
    "search",
    params,
    () =>
      API.get(
        `/posts/search/${encodeURIComponent(query)}`,
        {
          params: {
            limit,
            includeSnippet: includeSnippet ? 1 : 0,
          },
          signal,
          timeout: 12000,
          __publicRetry: true,
        },
      ),
    { signal },
  );
};

export const getPostNavigation = (slug) => {
  const encodedSlug = encodeURIComponent(slug);

  return dedupePublicGet(`navigation:${slug}`, () =>
    requestSameOriginPublicData(
      "navigation",
      { slug },
      () => API.get(`/posts/navigation/${encodedSlug}`, { __publicRetry: true }),
    ),
  );
};

/* =========================
   Admin posts
========================= */

export const getAdminPosts = (
  token,
  {
    page = 1,
    limit = 100,
    search = "",
    category = "All",
    status = "All",
    sort = "latest",
  } = {},
) => {
  const normalizedCategory = category === "All" ? "" : category;
  const normalizedStatus = status === "All" ? "" : status;
  const key = `admin-posts:${page}:${limit}:${search}:${normalizedCategory}:${normalizedStatus}:${sort}`;

  return dedupeAdminGet(key, () =>
    API.get("/posts/admin/all", {
      params: {
        page,
        limit,
        search,
        category: normalizedCategory,
        status: normalizedStatus,
        sort,
      },
      ...authConfig(token),
    }),
  );
};

export const getPostStats = (token) =>
  dedupeAdminGet("admin-stats", () =>
    API.get("/posts/admin/stats", authConfig(token)),
  );

export const getPostById = (
  id,
  token,
) =>
  API.get(
    `/posts/id/${encodeURIComponent(
      id,
    )}`,
    authConfig(token),
  );

export const addPost = (
  postData,
  token,
) =>
  afterAdminMutation(
    API.post(
      "/posts",
      postData,
      authConfig(token),
    ),
  );

export const updatePost = (
  id,
  postData,
  token,
) =>
  afterAdminMutation(
    API.put(
      `/posts/update/${encodeURIComponent(
        id,
      )}`,
      postData,
      authConfig(token),
    ),
  );

export const duplicatePost = (
  id,
  token,
) =>
  afterAdminMutation(
    API.post(
      `/posts/duplicate/${encodeURIComponent(
        id,
      )}`,
      {},
      authConfig(token),
    ),
  );

export const deletePost = (
  id,
  token,
) =>
  afterAdminMutation(
    API.delete(
      `/posts/delete/${encodeURIComponent(
        id,
      )}`,
      authConfig(token),
    ),
  );

/* =========================
   External API URLs
========================= */

export const getApiOrigin = () =>
  API_BASE_URL.replace(
    /\/api\/?$/,
    "",
  );

export const getRssUrl = (
  categorySlug = "",
) => {
  const baseUrl =
    `${API_BASE_URL}/rss`;

  if (!categorySlug) {
    return baseUrl;
  }

  return `${baseUrl}/category/${encodeURIComponent(
    categorySlug,
  )}`;
};

export default API;